import { useRef, useState, useEffect, KeyboardEvent, ClipboardEvent } from 'react'

interface OTPInputProps {
  length?: number
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  autoFocus?: boolean
}

export default function OTPInput({
  length = 7,
  value,
  onChange,
  disabled = false,
  autoFocus = true
}: OTPInputProps) {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length)
  }, [length])

  // Update internal state when value prop changes
  useEffect(() => {
    const otpArray = value.split('').slice(0, length)
    const paddedArray = [...otpArray, ...Array(length - otpArray.length).fill('')]
    setOtp(paddedArray)
  }, [value, length])

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [autoFocus])

  const handleChange = (index: number, inputValue: string) => {
    // Only allow alphanumeric characters
    const sanitizedValue = inputValue.toUpperCase().replace(/[^A-Z0-9]/g, '')

    if (sanitizedValue.length === 0) {
      // Handle deletion
      const newOtp = [...otp]
      newOtp[index] = ''
      setOtp(newOtp)
      onChange(newOtp.join(''))
      return
    }

    // Handle single character input
    if (sanitizedValue.length === 1) {
      const newOtp = [...otp]
      newOtp[index] = sanitizedValue
      setOtp(newOtp)
      onChange(newOtp.join(''))

      // Auto-focus next input
      if (index < length - 1 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus()
      }
      return
    }

    // Handle paste of multiple characters
    if (sanitizedValue.length > 1) {
      const chars = sanitizedValue.split('').slice(0, length)
      const newOtp = [...otp]

      chars.forEach((char, i) => {
        if (index + i < length) {
          newOtp[index + i] = char
        }
      })

      setOtp(newOtp)
      onChange(newOtp.join(''))

      // Focus last filled input or next empty one
      const nextIndex = Math.min(index + chars.length, length - 1)
      inputRefs.current[nextIndex]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        // If current field is empty, move to previous field
        inputRefs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain').toUpperCase().replace(/[^A-Z0-9]/g, '')

    if (pastedData) {
      const chars = pastedData.split('').slice(0, length)
      const newOtp = Array(length).fill('')

      chars.forEach((char, i) => {
        newOtp[i] = char
      })

      setOtp(newOtp)
      onChange(newOtp.join(''))

      // Focus last filled input
      const lastIndex = Math.min(chars.length - 1, length - 1)
      inputRefs.current[lastIndex]?.focus()
    }
  }

  const handleFocus = (index: number) => {
    inputRefs.current[index]?.select()
  }

  return (
    <div className="flex gap-2 justify-center">
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el
          }}
          type="text"
          inputMode="text"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          className={`
            w-12 h-14 text-center text-2xl font-bold
            border-2 rounded-lg
            transition-all duration-200
            ${digit
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-gray-300 bg-white text-gray-700'
            }
            ${disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:border-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none'
            }
          `}
          aria-label={`OTP digit ${index + 1}`}
        />
      ))}
    </div>
  )
}
